import React from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ProductStatusId } from "../../../js/models/maxEnums";
import { CheckoutOrderViewItem } from "./checkoutOrderView";

interface ProblematicItemsWarningModalProperties {
    readonly checkoutUrl: string;
    readonly items: CheckoutOrderViewItem[];
    readonly onClose: () => void;
}

const isNotWorkingItemsWarning = (items: CheckoutOrderViewItem[]): boolean => {
    for (const item of items) {
        if (item.productStatusId === ProductStatusId.NotWorking) {
            return true;
        }
    }

    return false;
};

const isUnknownItemsWarning = (items: CheckoutOrderViewItem[]): boolean => {
    for (const item of items) {
        if (item.productStatusId === ProductStatusId.Unknown) {
            return true;
        }
    }

    return false;
};

const scrollToTop = (): void => {
    window.scrollTo(0, 0);
};

export const isProblematicItemsWarning = (items: CheckoutOrderViewItem[]): boolean => isNotWorkingItemsWarning(items) || isUnknownItemsWarning(items);

export default function ProblematicItemsWarningModal({ checkoutUrl, items, onClose }: ProblematicItemsWarningModalProperties): React.JSX.Element | undefined {
    const { t } = useTranslation("basket");
    const [modalIsOpen, setModalIsOpen] = React.useState(true);
    const closeModal = (): void => {
        setModalIsOpen(false);
        onClose();
    };

    if (!isProblematicItemsWarning(items)) {
        return undefined;
    }

    return (
        <Modal
            id="products-sold-confirm"
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            className="modal-dialog modal-transition cart-modal-info"
            overlayClassName="react-modal-backdrop modal-transition"
        >
            <div className="modal-content">
                <div className="modal-header empty">
                    <button type="button" className="close" onClick={closeModal}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-12 col-sm-12">
                            <div className="well well-transparent nopadding cart-modal-info-well fm-modal-box">
                                <div className="cart-modal-info-box">
                                    <p className="fakeh3">
                                        {t("modalTitle")}
                                    </p>
                                    <p className="fakeh3 subheading">
                                        {t("subheading")}
                                    </p>

                                    {isNotWorkingItemsWarning(items) && (
                                        <p className="products-not-working-note">
                                            <span className="cart-item-quality-red in-modal">{t("nonFunctionalState")}</span>{t("nonFunctionalNote")}
                                        </p>
                                    )}

                                    {isUnknownItemsWarning(items) && (
                                        <p className="products-unknown-note">
                                            {t("productMarkedAs")}<span className="cart-item-quality-grey in-modal">{t("unknownState")}</span>{t("unknownNote")}
                                        </p>
                                    )}

                                    <div className="cart-item-products">
                                        {items
                                            .filter(((item) => item.productStatusId === ProductStatusId.NotWorking || item.productStatusId === ProductStatusId.Unknown))
                                            .map((item) => (
                                                <div key={item.productId} className="cart-item-wrap product-item">
                                                    <div className="cart-item-inner">
                                                        <div className="cart-item-product-image">
                                                            <span className="cart-item-product-image-helper"></span>
                                                            {item.imageUrl !== null && <img src={item.imageUrl} alt={item.title} width="170" height="170" />}
                                                        </div>
                                                        <div className="cart-item-product-title-availability-labels">
                                                            <a href={item.url} target="_blank" rel="noreferrer">
                                                                {item.title.length > 55 ? `${item.title.slice(0, 55)}…` : item.title}
                                                            </a>
                                                            <br />
                                                            <div className="cart-item-quality">{t("state")}:&nbsp;<span className={classNames(
                                                                item.productStatusId === ProductStatusId.NotWorking && "cart-item-quality-red",
                                                                item.productStatusId === ProductStatusId.Unknown && "cart-item-quality-grey",
                                                            )}>{item.productStatus}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>

                                    <div className="modal-bottons-note">
                                        <p className="products-common-note">
                                            {t("commonNote")}
                                        </p>
                                        <div className="shop-button cart-modal-info-button">
                                            <a
                                                className="btn btn-danger btn-lg small-padding"
                                                onClick={() => {
                                                    closeModal();
                                                    setTimeout(() => {
                                                        scrollToTop();
                                                    }, 300);
                                                }}>
                                                {t("editBasketItems")}</a>
                                            <a className="btn btn-success btn-lg small-padding" href={checkoutUrl}>{t("understandProceedToCheckout")}</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
